/**
 * FleetUpgradeAllDialog — §22 destructive-confirmation gate for runtime
 * upgrades: the fleet-wide "Upgrade all" action, and (when
 * `runtimeLabel` is set) the per-row single-runtime "Upgrade".
 *
 * Upgrading cold-boots the running lexruntime(s) → it abruptly
 * interrupts in-flight agent operations and can cause data loss. This
 * dialog forces the operator to (1) pick a mode and (2) type an exact
 * confirmation phrase before the action is allowed:
 *
 *   FORCE UPGRADE NOW — cold-boots all runtimes immediately (the
 *                       destructive path; full warning applies).
 *   SOFT UPGRADE      — defers each runtime to its least-disruptive
 *                       idle moment (much lower data-loss risk).
 *
 * The actual fleet operation is NOT wired yet (operator hand-off:
 * buttons stay unbound until the upgrade contracts are confirmed).
 * `onConfirm(mode)` is invoked with the chosen mode so the parent can
 * stub it today and bind it later without touching this UX.
 *
 * Reused for the per-row single-runtime "Upgrade" action: pass
 * `runtimeLabel` to scope the copy to one container (title, warning,
 * and confirm-button wording switch from fleet-wide to single). The
 * 2-mode + type-to-confirm UX and every `data-testid` are identical
 * in both scopes.
 */

import { useEffect, useState } from "react";
import { AlertTriangle, RefreshCw, Zap } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

export type FleetUpgradeMode = "force" | "soft";

const CONFIRM_PHRASE = "I UNDERSTAND";

export interface FleetUpgradeAllDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Invoked with the chosen mode once the operator confirms. */
  onConfirm: (mode: FleetUpgradeMode) => void;
  /**
   * When set, the dialog is scoped to ONE runtime (per-row "Upgrade"):
   * copy/title/confirm-button switch from fleet-wide to single. Absent
   * ⇒ the original fleet-wide "Upgrade all" scope.
   */
  runtimeLabel?: string;
  /**
   * How many of the affected runtimes are LOCAL (docker) rows. Drives the
   * localdev disclosure: those roll by a different mechanism than production
   * and can never be idle-deferred (Gap C closed 2026-07-20).
   */
  dockerCount?: number;
  /** Total affected runtimes — with `dockerCount`, tells us if ALL are local. */
  totalCount?: number;
}

export function FleetUpgradeAllDialog({
  open,
  onOpenChange,
  onConfirm,
  runtimeLabel,
  dockerCount = 0,
  totalCount,
}: FleetUpgradeAllDialogProps) {
  // Soft/idle is MEANINGLESS for a docker row: its target is the local image,
  // so there is nothing to defer onto. When every affected runtime is local we
  // disable Soft rather than offer a choice that silently cold-boots anyway —
  // that silent degrade is the same class of dishonesty as the no-op this
  // dialog's fix removed.
  const allDocker = dockerCount > 0 && totalCount != null && dockerCount >= totalCount;
  const single = runtimeLabel != null && runtimeLabel !== "";
  // Default to the SAFER mode so a careless Enter never cold-boots.
  const [mode, setMode] = useState<FleetUpgradeMode>("soft");
  const [confirmText, setConfirmText] = useState("");

  // Reset every time the dialog is (re)opened/closed.
  useEffect(() => {
    if (!open) {
      setMode("soft");
      setConfirmText("");
    }
  }, [open]);

  // When Soft is unavailable (an all-local scope), the selection MUST move to
  // force — otherwise the confirm button keeps reading "Schedule soft upgrade"
  // while the runtimes are cold-booted, i.e. the button would name an action
  // the system does not perform. Same class of dishonesty as the silent no-op
  // this dialog exists to make impossible.
  useEffect(() => {
    if (open && allDocker && mode === "soft") setMode("force");
  }, [open, allDocker, mode]);

  const phraseOk = confirmText.trim() === CONFIRM_PHRASE;

  const ModeCard = ({
    value,
    icon: Icon,
    title,
    body,
    danger,
    disabled,
    disabledReason,
  }: {
    value: FleetUpgradeMode;
    icon: typeof Zap;
    title: string;
    body: string;
    danger?: boolean;
    disabled?: boolean;
    disabledReason?: string;
  }) => {
    const selected = mode === value;
    return (
      <button
        type="button"
        disabled={disabled}
        title={disabled ? disabledReason : undefined}
        onClick={() => !disabled && setMode(value)}
        data-testid={`fleet-upgrade-mode-${value}`}
        aria-pressed={selected}
        className={
          "flex-1 rounded-lg border p-4 text-left transition-colors " +
          (selected
            ? danger
              ? "border-critical bg-critical/10 ring-1 ring-critical/40"
              : "border-primary bg-signal-soft ring-1 ring-primary/40"
            : "border-border bg-panel-2 hover:bg-accent") +
          (disabled ? " cursor-not-allowed opacity-50 hover:bg-panel-2" : "")
        }
      >
        <div
          className={
            "flex items-center gap-1.5 text-sm font-semibold " +
            (selected
              ? danger
                ? "text-critical"
                : "text-primary"
              : "text-foreground")
          }
        >
          <Icon
            className={"h-4 w-4 " + (danger ? "text-critical" : "text-primary")}
          />
          {title}
        </div>
        <p className="mt-1 text-xs text-muted">{body}</p>
        {disabled && disabledReason ? (
          <p className="mt-1 text-xs italic text-muted">{disabledReason}</p>
        ) : null}
      </button>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg space-y-4"
        data-testid="fleet-upgrade-dialog"
      >
        <DialogHeader className="items-center sm:text-center">
          <DialogTitle className="flex items-center justify-center gap-2 text-2xl text-foreground">
            <AlertTriangle className="h-6 w-6 text-critical" />
            {single ? "Upgrade runtime" : "Upgrade all runtimes"}
          </DialogTitle>
          <DialogDescription>
            {single ? (
              <>
                This affects the runtime{" "}
                <strong className="font-mono text-foreground">
                  {runtimeLabel}
                </strong>
                .
              </>
            ) : (
              <>
                This affects <strong>every deployed lexruntime</strong> at once.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <div
          className="rounded-md border border-critical/40 bg-critical/10 p-3 text-sm text-critical"
          data-testid="fleet-upgrade-warning"
        >
          <p className="font-medium">
            {single
              ? "This is a disruptive operation for this runtime."
              : "This is a risky, fleet-wide operation."}
          </p>
          <ul className="mt-1.5 list-disc space-y-0.5 pl-5 text-xs">
            <li>
              <strong>Force</strong> cold-boots{" "}
              {single ? "this runtime" : "every running runtime"} immediately.
            </li>
            <li>
              In-flight agent operations are <strong>abruptly interrupted</strong>{" "}
              — unsaved work in progress can be <strong>lost</strong>.
            </li>
            <li>
              {single ? "The user" : "Users"} mid-session will be disconnected
              and must reconnect.
            </li>
          </ul>
        </div>

        {/* LOCALDEV DISCLOSURE (operator ruling 2026-07-20). Local runtimes roll
            by a different mechanism than production, and the operator must be
            told BEFORE confirming — an invisible mode difference is exactly how
            a fleet-wide no-op went unnoticed for a session. */}
        {dockerCount > 0 ? (
          <div
            data-testid="fleet-upgrade-docker-notice"
            className="rounded-lg border border-border bg-panel-2 p-3 text-xs text-muted"
          >
            <p className="font-semibold text-foreground">
              {allDocker
                ? `All ${dockerCount} runtime(s) in this scope are local (Docker).`
                : `${dockerCount} of these runtime(s) are local (Docker).`}
            </p>
            <p className="mt-1">
              Local runtimes roll <strong>differently from production</strong>. In Azure an
              upgrade rolls onto a <strong>published image tag</strong> and can wait for each
              runtime&apos;s idle moment. Local runtimes have no image registry — they are{" "}
              <strong>recreated immediately on whatever <code>lexruntime:local</code> currently
              is</strong>, which is why <em>Soft (idle)</em> does not apply to them and they are
              always cold-booted.
            </p>
            <p className="mt-1">
              Their on-disk state (cases, config, cookies) is preserved across the recreate.
            </p>
          </div>
        ) : null}

        <div className="flex gap-3">
          <ModeCard
            value="force"
            icon={Zap}
            title="Force upgrade now"
            body="Cold-boots all runtimes immediately. Maximum disruption — the warning above applies in full."
            danger
          />
          <ModeCard
            value="soft"
            icon={RefreshCw}
            title="Soft upgrade (idle)"
            body="Defers each runtime to its least-disruptive idle moment. Much lower data-loss risk."
            disabled={allDocker}
            disabledReason={
              allDocker
                ? "Not available: local (Docker) runtimes have no published target to wait for — they are always recreated immediately."
                : undefined
            }
          />
        </div>

        <div className="space-y-1">
          <label
            htmlFor="fleet-upgrade-confirm"
            className="text-xs font-medium text-muted"
          >
            Type{" "}
            <span className="font-mono font-semibold text-foreground">
              {CONFIRM_PHRASE}
            </span>{" "}
            to enable the action:
          </label>
          <input
            id="fleet-upgrade-confirm"
            type="text"
            autoComplete="off"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={CONFIRM_PHRASE}
            data-testid="fleet-upgrade-confirm-input"
            className="w-full rounded-md border border-border bg-panel-2 px-3 py-2 text-sm text-foreground outline-none placeholder:text-faint focus:border-primary focus:ring-1 focus:ring-primary/30"
          />
        </div>

        <DialogFooter>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            data-testid="fleet-upgrade-cancel"
            className="rounded-md border border-border bg-panel-2 px-3 py-1.5 text-sm font-medium text-muted transition-colors hover:bg-accent hover:text-foreground"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!phraseOk}
            onClick={() => phraseOk && onConfirm(mode)}
            data-testid="fleet-upgrade-confirm"
            className={
              "rounded-md px-3 py-1.5 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40 " +
              (mode === "force"
                ? "bg-critical text-white hover:bg-critical/90"
                : "bg-primary text-primary-foreground hover:bg-primary/90")
            }
          >
            {mode === "force"
              ? single
                ? "Force upgrade now"
                : "Force upgrade all now"
              : "Schedule soft upgrade"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
